const CHART_TOP_PADDING = 10;
const CHART_BOTTOM_PADDING = 10;

export interface MarketValueChartBackdropPosition {
	readonly marketValue: number;
	readonly invested: number | null;
}

export interface MarketValueChartBackdropPaths {
	marketValuePath: string;
	investedPath: string | undefined;
}

export const buildMarketValueChartBackdropPaths = (
	historicalPositions: readonly MarketValueChartBackdropPosition[],
	width: number,
	height: number
): MarketValueChartBackdropPaths | undefined => {
	if (historicalPositions.length < 2 || width <= 0 || height <= 0) {
		return undefined;
	}

	const marketValues = historicalPositions.map(x => x.marketValue);
	const investedValues = historicalPositions.every(x => x.invested !== null)
		? historicalPositions.map(x => x.invested as number)
		: undefined;

	const scaledValues = investedValues ? [...marketValues, ...investedValues] : marketValues;
	const minValue = Math.min(...scaledValues);
	const maxValue = Math.max(...scaledValues);

	return {
		marketValuePath: toPath(marketValues, minValue, maxValue, width, height),
		investedPath: investedValues ? toPath(investedValues, minValue, maxValue, width, height) : undefined
	};
};

const toPath = (values: number[], minValue: number, maxValue: number, width: number, height: number): string => {
	return values
		.map((value, index) => {
			const x = (index / (values.length - 1)) * width;
			const y = toY(value, minValue, maxValue, height);
			return `${index === 0 ? "M" : "L"}${x},${y}`;
		})
		.join(" ");
};

const toY = (value: number, minValue: number, maxValue: number, height: number): number => {
	if (minValue === maxValue) {
		return height / 2;
	}

	const ratio = (value - minValue) / (maxValue - minValue);
	return height - CHART_BOTTOM_PADDING - ratio * (height - CHART_TOP_PADDING - CHART_BOTTOM_PADDING);
};
